import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "@/components/AppShell";
import Skeleton from "@/components/ui/Skeleton";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import VerdictBadge from "@/components/VerdictBadge";
import StageBadge from "@/components/pipeline/StageBadge";
import StageTabs from "@/components/pipeline/StageTabs";
import api from "@/lib/api";
import { strings } from "@/i18n/strings";
import { formatScore, propertyTypeLabel } from "@/lib/format";
import { useFmt } from "@/lib/useFmt";
import { PropertyDetailLine, PropertyDescription, PropertyContactLine } from "@/components/property/PropertyMeta";
// Batch G + Hotfix — Intelligence is now a view *of* Portfolio rather
// than a separate page. These three tables + hero + view-tabs live in
// /components/intelligence and are reused as-is.
import IntelligenceHero from "@/components/intelligence/IntelligenceHero";
import IntelligenceViewTabs from "@/components/intelligence/IntelligenceViewTabs";
import IntelligencePricesTable from "@/components/intelligence/IntelligencePricesTable";
import IntelligenceMetricsTable from "@/components/intelligence/IntelligenceMetricsTable";
import IntelligenceVerdictsTable from "@/components/intelligence/IntelligenceVerdictsTable";
import RoleDot from "@/components/deal/RoleDot";
import StageFilterDropdown, { stageFilterLabel } from "@/components/StageFilterDropdown";
import StageBreakdownCard from "@/components/portfolio/StageBreakdownCard";
// Phase 43 Batch BE — canonical asking/counter resolvers. Replaces the
// inline fallback chain in recomputeOne that was missing user_target_offer
// (Phase 42 Finding F — /target-offer-applied deals saw counter == asking).
import { askingFor, counterFor } from "@/lib/dealPriceBasis";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  Building2,
  DollarSign,
  TrendingUp,
  Percent,
  ArrowRight,
  Loader2,
  Plus,
  Activity,
  Microscope,
} from "lucide-react";

const S = strings.portfolio;

const VERDICT_COLORS = {
  "Strong Buy": "#059669",
  Buy: "#10b981",
  Hold: "#f59e0b",
  Risky: "#ea580c",
  Reject: "#dc2626",
};

const FILTERS = ["All", "Strong Buy", "Buy", "Hold", "Risky", "Reject"];

function KpiCard({ label, value, sub, Icon, tone = "default", testid }) {
  const toneCls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50/40"
      : tone === "bad"
      ? "border-red-200 bg-red-50/40"
      : "border-slate-200 bg-white";
  return (
    <div data-testid={testid} className={`rounded-xl border p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${toneCls}`}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-slate-300" />}
      </div>
      <div className="mt-2 text-2xl font-bold tracking-tight text-slate-900 tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function SectionCard({ title, sub, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
      </div>
      {children}
    </div>
  );
}

function HorizontalBar({ label, pct, count }) {
  return (
    <div>
      <div className="flex justify-between text-xs">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="tabular-nums text-slate-500">
          {count} · {(pct * 100).toFixed(0)}%
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-indigo-600" style={{ width: `${pct * 100}%` }} />
      </div>
    </div>
  );
}

function PerformerCard({ data, tone, navigate, testid }) {
  const fmt = useFmt();
  if (!data) return null;
  const borderCls = tone === "good" ? "border-l-emerald-500" : "border-l-red-500";
  // Phase 36 Batch L — surface role + price-basis caption so the
  // best/worst pick is honestly explained ("this is the best at the
  // counter you set" vs "the best at asking" vs "the best from the
  // seller's POV"). Falls back gracefully if backend hasn't sent the
  // new fields yet (legacy clients).
  const basis = data.price_basis;
  const basisValue = data.price_basis_value;
  let basisCaption = null;
  if (basisValue) {
    const fmtPrice = `$${Math.round(basisValue).toLocaleString()}`;
    if (basis === "counter_offer") basisCaption = `evaluated at counter ${fmtPrice}`;
    else if (basis === "asking_seller_view" || basis === "asking") basisCaption = `evaluated at asking ${fmtPrice}`;
  }
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => navigate(`/deals/${data.deal_id}`)}
      className={`group flex w-full items-start justify-between gap-3 rounded-xl border border-slate-200 border-l-4 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md ${borderCls}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {/* Phase 36 Batch L — RoleDot next to the property address */}
          <RoleDot
            metrics={{ role: data.role, price_basis: data.price_basis, price_basis_value: data.price_basis_value }}
            role={data.role}
            testid={`${testid}-role-dot`}
          />
          <p className="truncate text-sm font-semibold text-slate-900">{data.address}</p>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <VerdictBadge verdict={data.verdict} />
          <span className="text-xs text-slate-500 tabular-nums">Score {formatScore(data.deal_score)}</span>
        </div>
        <div className="mt-2 text-xs text-slate-500">
          CF/mo {fmt.money(data.monthly_cash_flow)} · Cap {fmt.percent(data.cap_rate)}
        </div>
        {basisCaption ? (
          <div
            data-testid={`${testid}-basis-caption`}
            className="mt-1 text-[10px] uppercase tracking-wider text-slate-400"
          >
            {basisCaption}
          </div>
        ) : null}
      </div>
      <ArrowRight className="mt-1 h-4 w-4 flex-shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

// Phase 13 P0 hotfix — safe defaults so KPI strip never dereferences null
// when /portfolio/summary 4xx/5xx or returns null.
const PORTFOLIO_DEFAULT = {
  totals: {
    total_properties: 0, total_market_value: 0, total_purchase_basis: 0,
    total_monthly_rent: 0, total_monthly_cash_flow: 0,
    total_cash_invested: 0, total_loan_balance: 0,
  },
  averages: { avg_cap_rate: 0, avg_cash_on_cash: 0, avg_deal_score: 0, avg_dscr: 0 },
  risk_concentration: { by_state: [], by_property_type: [], high_risk_count: 0, low_dscr_count: 0 },
  best_performer: null,
  worst_performer: null,
  verdict_distribution: [],
};

export default function Portfolio() {
  const navigate = useNavigate();
  const fmt = useFmt();
  const [summary, setSummary] = useState(PORTFOLIO_DEFAULT);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [sort, setSort] = useState({ key: "deal_score", dir: "desc" });
  // Batch E2.1 — pipeline-stage filter via URL query string so refresh /
  // share / back-button just work. "all" maps to no filter param.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeStage = searchParams.get("stage") || "all";
  // Phase 36 Batch O — bucket filter (page-level). Default mirrors the
  // backend _DEFAULT_STAGE_PORTFOLIO = "owned" so the page lands on
  // "the actual portfolio I've closed on", not every prospect mixed
  // in. Persisted to URL `?bucket=` for refresh/share/back-button.
  const bucketFilter = searchParams.get("bucket") || "owned";
  // Batch G + Hotfix — Intelligence view-mode lives on the URL too so
  // refresh / share / back-button just work. "basic" = current Portfolio
  // chrome (KPIs + charts + table). Anything else swaps the upper sections
  // for the Intelligence hero + the matching Intelligence table.
  const viewParam = searchParams.get("view") || "basic";
  const view = ["basic", "prices", "metrics", "verdicts"].includes(viewParam) ? viewParam : "basic";
  const isIntelView = view !== "basic";

  // Intelligence-specific state — only populated when view !== "basic".
  // Kept separate from the portfolio summary so switching back to Basic
  // doesn't re-fetch the heavy per-deal recompute.
  const [intelSummary, setIntelSummary] = useState(null);
  const [intelRows, setIntelRows] = useState({}); // { dealId: { at_asking, at_counter } }
  const [intelLoading, setIntelLoading] = useState(false);
  // Batch H — per-deal criteria-eval summary (passed/evaluated/top_failures).
  // Lazily populated when entering the Verdicts view so the chip column has data.
  const [criteriaByDeal, setCriteriaByDeal] = useState({});

  useEffect(() => {
    (async () => {
      try {
        // Phase 36 Batch O — fetches honour the bucket-level filter.
        // The legacy `?pipeline_stage=<raw>` filter (from StageTabs) also
        // composes if set, so the user can drill from "Owned bucket"
        // into "Owned (raw stage)" and the backend will AND the two.
        const dealsParts = [];
        if (activeStage !== "all") dealsParts.push(`pipeline_stage=${activeStage}`);
        if (bucketFilter)          dealsParts.push(`stage=${bucketFilter}`);
        const dealsPath = `/deals${dealsParts.length ? `?${dealsParts.join("&")}` : ""}`;
        const summaryPath = `/portfolio/summary?stage=${bucketFilter}`;
        const [s, d] = await Promise.all([api.get(summaryPath), api.get(dealsPath)]);
        // Merge over defaults so any missing sub-key (e.g. averages on a fresh
        // account) won't trip JSX that reads summary.totals.X / averages.X.
        const data = s?.data || {};
        setSummary({
          ...PORTFOLIO_DEFAULT,
          ...data,
          totals: { ...PORTFOLIO_DEFAULT.totals, ...(data.totals || {}) },
          averages: { ...PORTFOLIO_DEFAULT.averages, ...(data.averages || {}) },
          risk_concentration: { ...PORTFOLIO_DEFAULT.risk_concentration, ...(data.risk_concentration || {}) },
        });
        setDeals(Array.isArray(d?.data) ? d.data : []);
      } catch {
        setSummary(PORTFOLIO_DEFAULT);
        setDeals([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [activeStage, bucketFilter]);

  const handleStageChange = (stageId) => {
    if (stageId === "all") {
      // Drop the param entirely so the URL stays clean.
      const next = new URLSearchParams(searchParams);
      next.delete("stage");
      setSearchParams(next, { replace: true });
    } else {
      const next = new URLSearchParams(searchParams);
      next.set("stage", stageId);
      setSearchParams(next, { replace: true });
    }
  };

  // Batch G + Hotfix — view-mode toggle. "basic" drops the param so
  // /portfolio bookmarks stay clean.
  const handleViewChange = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "basic" || !next) params.delete("view");
    else params.set("view", next);
    setSearchParams(params, { replace: true });
  };

  // Phase 36 Batch O — bucket dropdown setter. Persists to `?bucket=`.
  // We DON'T touch ?stage= or ?view= so the user can layer the bucket
  // filter on top of the existing per-stage drill-in or intelligence
  // view without losing state.
  const handleBucketChange = (next) => {
    const params = new URLSearchParams(searchParams);
    if (!next || next === "owned") params.delete("bucket"); // owned is the default
    else params.set("bucket", next);
    setSearchParams(params, { replace: true });
  };

  // Intelligence per-deal recompute. Stable callback so the load +
  // counter-apply effects share one POST helper.
  const recomputeOne = useCallback(async (deal) => {
    // Phase 43 Batch BE — canonical helpers. Pre-Phase 43 the counter
    // chain skipped `user_target_offer`, so /target-offer-applied
    // deals' Verdicts table row showed at_counter == at_asking with
    // zero verdict shift (Phase 42 Finding F second-order effect).
    const asking = askingFor(deal);
    const counter = counterFor(deal);
    if (asking <= 0) return null;
    try {
      const [a, b] = await Promise.all([
        api.post("/intelligence/deal-at-price", { deal_id: deal.id, price: asking }),
        api.post("/intelligence/deal-at-price", { deal_id: deal.id, price: counter }),
      ]);
      return { at_asking: a.data, at_counter: b.data };
    } catch (err) {
      console.warn("recomputeOne failed", deal.id, err);
      return null;
    }
  }, []);

  // Lazy-load intel data only when the user actually switches into an
  // intelligence view. Re-runs when `deals` changes (e.g. stage filter
  // narrows the list) so the hero KPIs match the visible book.
  useEffect(() => {
    if (!isIntelView || deals.length === 0) return;
    let cancelled = false;
    setIntelLoading(true);
    (async () => {
      try {
        // Phase 43 Batch BE — Phase 42 Finding E: pre-Phase 43 this
        // fetch dropped the bucket filter, so the Hero strip
        // aggregated ALL deals in the book regardless of which Stage
        // the user picked. Pipe the same filter the /deals fetch uses
        // (line 224) so the aggregates match the visible row set.
        const intelPath = bucketFilter
          ? `/intelligence/summary?stage=${encodeURIComponent(bucketFilter)}`
          : "/intelligence/summary";
        const sumResp = await api.get(intelPath);
        if (cancelled) return;
        setIntelSummary(sumResp.data);
        if (view === "metrics" || view === "verdicts") {
          const results = await Promise.all(
            deals.map(async (d) => [d.id, await recomputeOne(d)]),
          );
          if (cancelled) return;
          const next = {};
          for (const [id, val] of results) if (val) next[id] = val;
          setIntelRows(next);
        }
        // Batch H — pre-fetch criteria-eval summaries for the Verdicts
        // chip column. One call per deal (template-only, no LLM), all
        // in parallel — finishes in ~1-2s for the 200-deal cap.
        if (view === "verdicts") {
          const critResults = await Promise.all(
            deals.map(async (d) => {
              try {
                const r = await api.get(`/deals/${d.id}/criteria-eval`);
                return [d.id, r.data.summary];
              } catch (err) {
                return [d.id, null];
              }
            }),
          );
          if (cancelled) return;
          const map = {};
          for (const [id, val] of critResults) if (val) map[id] = val;
          setCriteriaByDeal(map);
        }
      } catch (err) {
        console.warn("intel load failed", err);
      } finally {
        if (!cancelled) setIntelLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isIntelView, view, deals, recomputeOne, bucketFilter]);

  // Counter saved from Prices view → refresh metrics + summary in place.
  const handleCounterSaved = useCallback(
    async (dealId, newCounter) => {
      setDeals((prev) =>
        prev.map((d) => (d.id === dealId ? { ...d, counter_offer_price: newCounter } : d)),
      );
      const target = deals.find((d) => d.id === dealId);
      if (!target) return;
      const patched = { ...target, counter_offer_price: newCounter };
      const [row, sumResp] = await Promise.all([
        recomputeOne(patched),
        api.get("/intelligence/summary"),
      ]);
      if (row) setIntelRows((prev) => ({ ...prev, [dealId]: row }));
      setIntelSummary(sumResp.data);
    },
    [deals, recomputeOne],
  );

  // Phase 36 Batch N retest fix — verdict counts for the filter pills,
  // computed off the LIVE role-aware verdict so the number next to each
  // pill matches what the user gets when they click it. Stored
  // ai_summary verdicts (frozen at original analysis time) are
  // intentionally ignored here.
  const verdictPillCounts = useMemo(() => {
    const c = { All: deals.length, "Strong Buy": 0, "Buy": 0, "Hold": 0, "Risky": 0, "Reject": 0 };
    for (const d of deals) {
      const v = d.metrics_at_role?.verdict ?? d.verdict;
      if (v && Object.prototype.hasOwnProperty.call(c, v)) c[v] += 1;
    }
    return c;
  }, [deals]);

  const filtered = useMemo(() => {
    let list = deals;
    if (filter !== "All") list = list.filter((d) => (d.metrics_at_role?.verdict ?? d.verdict) === filter);
    const { key, dir } = sort;
    list = [...list].sort((a, b) => {
      const av = a[key] ?? 0;
      const bv = b[key] ?? 0;
      return dir === "asc" ? av - bv : bv - av;
    });
    return list;
  }, [deals, filter, sort]);

  // Phase 36 Batch N retest fix — Intelligence subtables (Verdicts /
  // Metrics / Prices) must honour the same filter pill as the Basic
  // table. Previously sourced from `deals` (unfiltered), which is why
  // clicking "Hold" still showed every deal in the Verdicts grid.
  // Declared AFTER `filtered` so the React hook order stays stable
  // (prior placement triggered 'Cannot access filtered before
  // initialization' at runtime).
  const intelRowsForTables = useMemo(
    () =>
      filtered
        .map((d) => {
          const r = intelRows[d.id];
          if (!r) return null;
          return { deal: d, at_asking: r.at_asking, at_counter: r.at_counter };
        })
        .filter(Boolean),
    [filtered, intelRows],
  );

  const setSortKey = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));
  };

  if (loading) {
    return (
      <AppShell>
        <div className="space-y-10" data-testid="portfolio-loading">
          <div className="space-y-3">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-10 w-72" />
            <Skeleton className="h-4 w-96" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Skeleton.Card />
            <Skeleton.Card />
            <Skeleton.Card />
            <Skeleton.Card />
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-4">
              <Skeleton className="h-4 w-32" />
            </div>
            <Skeleton.TableRows rows={5} cols={6} />
          </div>
        </div>
      </AppShell>
    );
  }

  const total = summary?.totals?.total_properties || 0;
  if (total === 0) {
    return (
      <AppShell>
        <div className="mx-auto max-w-2xl py-16 text-center" data-testid="portfolio-empty">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
            <Building2 className="h-6 w-6" />
          </div>
          <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-900">{S.empty_title}</h1>
          <p className="mt-2 text-sm text-slate-500">{S.empty_body}</p>
          <Button
            onClick={() => navigate("/deals/new")}
            className="mt-6 bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
            data-testid="portfolio-empty-cta"
          >
            <Plus className="mr-2 h-4 w-4" />
            {S.empty_cta}
          </Button>
        </div>
      </AppShell>
    );
  }

  const t = summary.totals;
  const avg = summary.averages;
  const rc = summary.risk_concentration || {};
  const stateCount = (rc.by_state || []).length;
  const cfTone = t.total_monthly_cash_flow >= 0 ? "good" : "bad";

  const cashFlowChartData = deals.map((d) => ({
    id: d.id,  // Phase 36 Batch 2 — stamped so the <Cell> render below
               // can use a stable per-deal key instead of the row index.
    address: (d.property?.address || "—").slice(0, 22),
    full: d.property?.address,
    // Phase 36 Batch K — chart honours role-aware cash flow.
    cf: d.metrics_at_role?.monthly_cash_flow ?? d.cash_flow_monthly ?? 0,
    // Phase 36 Batch N — chart verdict color also honours the live verdict.
    verdict: d.metrics_at_role?.verdict ?? d.verdict,
  }));

  const verdictData = (summary.verdict_distribution || []).filter((v) => v.count > 0);

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl space-y-10 fade-in" data-testid="portfolio-root">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-600">{S.title}</p>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">{S.title}</h1>
            <p className="mt-3 max-w-2xl text-base text-slate-600">{S.subtitle}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StageFilterDropdown
              value={bucketFilter}
              onChange={handleBucketChange}
              testid="portfolio-stage-filter"
            />
            <Button
              data-testid="portfolio-compare-cta"
              variant="outline"
              onClick={() => navigate("/compare")}
              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            >
              <Microscope className="mr-1 h-4 w-4" /> Compare deals
            </Button>
            <Button
              data-testid="portfolio-new-deal"
              onClick={() => navigate("/deals/new")}
              className="bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
            >
              <Plus className="mr-1 h-4 w-4" />
              {strings.common.new_deal_cta}
            </Button>
          </div>
        </div>

        {/* Phase 36 Batch O — active-filter banner. Hidden for the
            "owned" default so a fresh Portfolio visit stays clean. */}
        {bucketFilter && bucketFilter !== "owned" ? (
          <div
            data-testid="portfolio-stage-filter-banner"
            className="-mt-6 inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700"
          >
            <span>Showing: {stageFilterLabel(bucketFilter)}</span>
            <button
              type="button"
              onClick={() => handleBucketChange("owned")}
              data-testid="portfolio-stage-filter-clear"
              className="text-indigo-600 hover:text-indigo-800"
            >
              clear ×
            </button>
          </div>
        ) : null}

        {/* Batch G + Hotfix — view-mode toggle. Always visible so the
            user can flip between Basic ↔ Prices/Metrics/Verdicts without
            leaving the page. */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <IntelligenceViewTabs value={view} onChange={handleViewChange} />
          {isIntelView && intelLoading ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Re-underwriting…
            </span>
          ) : null}
        </div>

        {/* Intelligence hero — replaces the upper Portfolio sections
            (KPIs + charts + risk) when the user is in an intel view. */}
        {isIntelView ? (
          <IntelligenceHero summary={intelSummary} stageFilter={bucketFilter} />
        ) : (
        <>
        {/* Phase 36 Batch O — Stage breakdown card. Top of the Overview
            so the user lands on a complete picture of "where my deals
            live across the funnel". Clicking a tile narrows the bucket
            filter (KPIs + Deals table refetch accordingly). */}
        <StageBreakdownCard activeBucket={bucketFilter} onPick={handleBucketChange} />

        {/* KPIs */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            label={S.kpis.properties}
            value={t.total_properties}
            sub={stateCount > 0 ? `across ${stateCount} state${stateCount > 1 ? "s" : ""}` : null}
            Icon={Building2}
            testid="pkpi-properties"
          />
          <KpiCard
            label={S.kpis.market_value}
            value={fmt.money(t.total_market_value)}
            sub={`basis ${fmt.money(t.total_purchase_basis)}`}
            Icon={DollarSign}
            testid="pkpi-market-value"
          />
          <KpiCard
            label={S.kpis.monthly_cash_flow}
            value={fmt.money(t.total_monthly_cash_flow, { cents: true })}
            tone={cfTone}
            Icon={TrendingUp}
            testid="pkpi-cashflow"
          />
          <KpiCard
            label={S.kpis.avg_cap_rate}
            value={fmt.percent(avg.avg_cap_rate)}
            sub={`avg DSCR ${avg.avg_dscr ? avg.avg_dscr.toFixed(2) : "—"}x`}
            Icon={Percent}
            testid="pkpi-cap"
          />
        </div>

        {/* Phase 47.3 — portfolio avg PSF/PSM chip. Renders only when at
            least one rental contributed (backend skips flips/BRRRRs from
            the avg). Falls back to '—' when the rental count is 0. */}
        {(avg?.avg_price_per_sqft != null || avg?.avg_price_per_sqm != null) ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="portfolio-psf-row">
            <KpiCard
              label="Avg $/sqft (rentals)"
              value={
                avg.avg_price_per_sqft != null
                  ? `$${Math.round(avg.avg_price_per_sqft).toLocaleString("en-US")}`
                  : "—"
              }
              sub={
                avg.avg_price_per_sqm != null
                  ? `≈ $${Math.round(avg.avg_price_per_sqm).toLocaleString("en-US")}/sqm`
                  : null
              }
              Icon={DollarSign}
              testid="pkpi-avg-psf"
            />
          </div>
        ) : null}

        {/* Phase 19 — Owned-only KPIs (visible only when ≥1 owned deal). */}
        {(() => {
          const owned = (deals || []).filter((d) => d?.is_owned);
          if (owned.length === 0) return null;
          // Roll up appreciation + annualized return from cached
          // historical_performance snapshots on each deal. Snapshots are
          // populated by GET /api/deals/{id}/historical-performance — if the
          // user hasn't opened the Performance tab yet for a particular deal
          // the snapshot may be missing, in which case we skip that deal.
          let totalAppr = 0;
          let totalBasis = 0;
          let annSum = 0;
          let annN = 0;
          for (const d of owned) {
            const hp = d?.historical_performance;
            if (hp && typeof hp.total_appreciation === "number") {
              totalAppr += hp.total_appreciation;
              totalBasis += Number(d?.purchase_price_paid) || 0;
            }
            if (hp && typeof hp.annualized_return_pct === "number") {
              annSum += hp.annualized_return_pct;
              annN += 1;
            }
          }
          const apprPct = totalBasis > 0 ? totalAppr / totalBasis : null;
          const avgAnn = annN > 0 ? annSum / annN : null;
          return (
            <div
              className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
              data-testid="portfolio-phase19-kpis"
            >
              <KpiCard
                label="Portfolio appreciation"
                value={
                  totalAppr >= 0
                    ? `+${fmt.money(totalAppr)}`
                    : `−${fmt.money(Math.abs(totalAppr))}`
                }
                sub={
                  apprPct == null
                    ? `${owned.length} owned (open Performance tab to fill data)`
                    : `${(apprPct * 100).toFixed(1)}% across ${owned.length} owned`
                }
                tone={totalAppr >= 0 ? "good" : "bad"}
                Icon={TrendingUp}
                testid="pkpi-portfolio-appreciation"
              />
              <KpiCard
                label="Avg annualized return"
                value={avgAnn == null ? "—" : `${(avgAnn * 100).toFixed(1)}%`}
                sub={avgAnn == null ? "data not yet computed" : "across owned properties"}
                Icon={Percent}
                tone={avgAnn != null && avgAnn >= 0.07 ? "good" : "default"}
                testid="pkpi-avg-annualized"
              />
            </div>
          );
        })()}

        {/* Charts row */}
        <div className="grid gap-5 lg:grid-cols-2">
          <SectionCard title={S.sections.cashflow_perf} sub={S.sections.cashflow_perf_sub} testid="portfolio-cashflow-chart">
            <div className="h-72">
              <ResponsiveContainer>
                <BarChart data={cashFlowChartData} margin={{ top: 8, right: 12, bottom: 28, left: 0 }}>
                  <XAxis dataKey="address" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-20} textAnchor="end" />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <RTooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                    formatter={(value) => [fmt.money(value, { cents: true }), "CF/mo"]}
                    labelFormatter={(_, p) => p?.[0]?.payload?.full || ""}
                  />
                  <Bar dataKey="cf" radius={[6, 6, 0, 0]}>
                    {cashFlowChartData.map((d) => (
                      // Phase 36 Batch 2 — stable key using the deal id
                      // (cashFlowChartData rows mirror the owned-deals
                      // list one-for-one).
                      <Cell key={d.id || d.full} fill={VERDICT_COLORS[d.verdict] || "#4f46e5"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>

          <SectionCard title={S.sections.verdict_dist} sub={S.sections.verdict_dist_sub} testid="portfolio-verdict-chart">
            <div className="h-72">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={verdictData}
                    dataKey="count"
                    nameKey="verdict"
                    innerRadius={56}
                    outerRadius={92}
                    paddingAngle={2}
                    label={(p) => `${p.verdict}`}
                  >
                    {verdictData.map((v) => (
                      // Phase 36 Batch 2 — `verdict` is the unique
                      // categorical key in this pie chart.
                      <Cell key={v.verdict} fill={VERDICT_COLORS[v.verdict] || "#94a3b8"} />
                    ))}
                  </Pie>
                  <RTooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>
        </div>

        {/* Risk + highlights row */}
        <div className="grid gap-5 lg:grid-cols-2">
          <SectionCard title={S.sections.risk_concentration} testid="portfolio-risk-concentration">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <h4 className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{S.sections.by_state}</h4>
                <div className="space-y-3">
                  {(rc.by_state || []).slice(0, 5).map((s) => (
                    <HorizontalBar key={s.state} label={s.state} pct={s.pct} count={s.count} />
                  ))}
                </div>
              </div>
              <div>
                <h4 className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{S.sections.by_type}</h4>
                <div className="space-y-3">
                  {(rc.by_property_type || []).slice(0, 6).map((s) => (
                    <HorizontalBar key={s.type} label={propertyTypeLabel(s.type)} pct={s.pct} count={s.count} />
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-100 pt-4 text-xs">
              <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-red-700">
                {rc.high_risk_count || 0} high-risk verdicts
              </span>
              <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-amber-700">
                {rc.low_dscr_count || 0} sub-1.0 DSCR
              </span>
            </div>
          </SectionCard>

          <SectionCard title={S.sections.highlights} testid="portfolio-highlights">
            <div className="grid gap-3">
              {summary.best_performer && (
                <div>
                  <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">{S.sections.best_performer}</h4>
                  <PerformerCard data={summary.best_performer} tone="good" navigate={navigate} testid="portfolio-best" />
                </div>
              )}
              {summary.worst_performer && (
                <div>
                  <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-red-700">{S.sections.worst_performer}</h4>
                  <PerformerCard data={summary.worst_performer} tone="bad" navigate={navigate} testid="portfolio-worst" />
                </div>
              )}
            </div>
          </SectionCard>
        </div>
        </>
        )}

        {/* All deals */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="portfolio-all-deals">
          <div className="border-b border-slate-200 px-5 pt-4">
            <StageTabs active={activeStage} onChange={handleStageChange} />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900">{S.sections.all_deals}</h2>
              <p className="mt-0.5 text-xs text-slate-500">{filtered.length} of {deals.length}</p>
            </div>
            <div className="flex flex-wrap gap-1">
              {FILTERS.map((f) => {
                const count = verdictPillCounts[f] ?? 0;
                return (
                  <button
                    key={f}
                    type="button"
                    data-testid={`filter-${f.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => setFilter(f)}
                    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-all active:scale-[0.97] ${
                      filter === f
                        ? "border-indigo-600 bg-indigo-600 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    {f}
                    {/* Phase 36 Batch N retest fix — live verdict count. */}
                    <span
                      data-testid={`filter-${f.replace(/\s+/g, "-").toLowerCase()}-count`}
                      className={`tabular-nums ${
                        filter === f ? "text-white/80" : "text-slate-400"
                      }`}
                    >
                      ({count})
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Table region — Basic shows the legacy desktop table +
              mobile cards. Intelligence views swap in the matching
              Prices / Metrics / Verdicts table built by Batch G. */}
          {isIntelView ? (
            <div className="p-4 sm:p-5">
              {view === "prices" ? (
                <IntelligencePricesTable deals={filtered} onSaved={handleCounterSaved} />
              ) : view === "metrics" ? (
                <IntelligenceMetricsTable rows={intelRowsForTables} />
              ) : (
                <IntelligenceVerdictsTable rows={intelRowsForTables} criteriaByDeal={criteriaByDeal} />
              )}
            </div>
          ) : (
          <>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="cursor-pointer text-right" onClick={() => setSortKey("purchase_price")}>
                    Price {sort.key === "purchase_price" && (sort.dir === "asc" ? "↑" : "↓")}
                  </TableHead>
                  <TableHead className="cursor-pointer text-right" onClick={() => setSortKey("cash_flow_monthly")}>
                    CF / mo {sort.key === "cash_flow_monthly" && (sort.dir === "asc" ? "↑" : "↓")}
                  </TableHead>
                  <TableHead className="cursor-pointer text-right" onClick={() => setSortKey("cap_rate")}>
                    Cap {sort.key === "cap_rate" && (sort.dir === "asc" ? "↑" : "↓")}
                  </TableHead>
                  <TableHead className="cursor-pointer text-right" onClick={() => setSortKey("deal_score")}>
                    Score {sort.key === "deal_score" && (sort.dir === "asc" ? "↑" : "↓")}
                  </TableHead>
                  <TableHead>Verdict</TableHead>
                  <TableHead>Manage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((d) => {
                  const cv = d.currency_view || {};
                  const fxBadge =
                    cv.local_currency && cv.home_currency && cv.local_currency !== cv.home_currency
                      ? cv.local_currency
                      : null;
                  return (
                  <TableRow
                    key={d.id}
                    data-testid={`prow-${d.id}`}
                    onClick={() => navigate(`/deals/${d.id}`)}
                    className="cursor-pointer hover:bg-slate-50"
                  >
                    <TableCell className="font-medium text-slate-900">
                      <div className="flex items-center gap-2">
                        <RoleDot
                          metrics={d.metrics_at_role}
                          role={d.role}
                          testid={`role-dot-portfolio-${d.id}`}
                        />
                        <span>{d.property?.address}</span>
                        {d.pipeline_stage ? (
                          <span onClick={(e) => e.stopPropagation()}>
                            <StageBadge stage={d.pipeline_stage} dealId={d.id} editable size="sm" />
                          </span>
                        ) : null}
                        {d.is_owned && d.purchase_year ? (
                          <span
                            data-testid={`owned-chip-${d.id}`}
                            title="You own this property"
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200"
                          >
                            Owned · {Math.max(0, new Date().getFullYear() - d.purchase_year)}y
                          </span>
                        ) : null}
                        {fxBadge ? (
                          <span
                            data-testid={`fx-pill-${d.id}`}
                            title={cv.fx_risk_warning || "Cross-currency exposure"}
                            className="inline-flex items-center gap-0.5 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-800"
                          >
                            FX {fxBadge}
                          </span>
                        ) : null}
                      </div>
                      <PropertyDetailLine d={d} fmt={fmt} />
                      <PropertyDescription d={d} fmt={fmt} />
                      <PropertyContactLine d={d} />
                    </TableCell>
                    <TableCell className="text-slate-700">{propertyTypeLabel(d.property?.property_type)}</TableCell>
                    {/* Phase 36 Batch M Bug 3 — role/ownership-aware price */}
                    <TableCell className="text-right tabular-nums">
                      <div>{fmt.money(d.metrics_at_role?.display_price ?? d.purchase_price)}</div>
                      {d.metrics_at_role?.display_price_label ? (
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">
                          {d.metrics_at_role.display_price_label}
                        </div>
                      ) : null}
                    </TableCell>
                    {/* Phase 36 Batch K — role-aware metric reads, legacy fallback */}
                    <TableCell className={`text-right tabular-nums font-medium ${(d.metrics_at_role?.monthly_cash_flow ?? d.cash_flow_monthly) >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                      {fmt.money(d.metrics_at_role?.monthly_cash_flow ?? d.cash_flow_monthly, { cents: true })}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmt.percent(d.metrics_at_role?.cap_rate ?? d.cap_rate)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{formatScore(d.metrics_at_role?.deal_score ?? d.deal_score)}</TableCell>
                    {/* Phase 36 Batch N retest fix — verdict badge reads
                        the LIVE role-aware verdict, falling back to the
                        stored field only when the new block is absent. */}
                    <TableCell><VerdictBadge verdict={d.metrics_at_role?.verdict ?? d.verdict} /></TableCell>
                    <TableCell>
                      <button
                        data-testid={`prow-manage-${d.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/manage/properties/${d.property_id}`);
                        }}
                        className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 hover:bg-emerald-100"
                      >
                        Manage
                      </button>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <ul className="divide-y divide-slate-200 md:hidden">
            {filtered.map((d) => (
              <li key={`m-${d.id}`} onClick={() => navigate(`/deals/${d.id}`)} className="cursor-pointer p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">{d.property?.address}</p>
                    {d.pipeline_stage ? (
                      <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                        <StageBadge stage={d.pipeline_stage} dealId={d.id} editable size="sm" />
                      </div>
                    ) : null}
                    {d.is_owned && d.purchase_year ? (
                      <span
                        data-testid={`owned-chip-mobile-${d.id}`}
                        className="mt-1 inline-flex items-center rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200"
                      >
                        Owned · {Math.max(0, new Date().getFullYear() - d.purchase_year)}y
                      </span>
                    ) : null}
                    <PropertyDetailLine d={d} fmt={fmt} />
                    <PropertyDescription d={d} fmt={fmt} />
                    <PropertyContactLine d={d} />
                  </div>
                  <VerdictBadge verdict={d.metrics_at_role?.verdict ?? d.verdict} />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <div><span className="text-slate-500">CF/mo</span><div className={`mt-0.5 font-medium tabular-nums ${(d.metrics_at_role?.monthly_cash_flow ?? d.cash_flow_monthly) >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmt.money(d.metrics_at_role?.monthly_cash_flow ?? d.cash_flow_monthly)}</div></div>
                  <div><span className="text-slate-500">Cap</span><div className="mt-0.5 font-medium tabular-nums text-slate-900">{fmt.percent(d.metrics_at_role?.cap_rate ?? d.cap_rate)}</div></div>
                  <div><span className="text-slate-500">Score</span><div className="mt-0.5 font-semibold tabular-nums text-slate-900">{formatScore(d.metrics_at_role?.deal_score ?? d.deal_score)}</div></div>
                </div>
              </li>
            ))}
          </ul>
          </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
